// Static content for the Folk homeowner docket.
// Rendered inside Portal.jsx. Client name is interpolated where relevant.

export const FOUNDERS_LETTER = ({ salutation }) => [
  `${salutation},`,
  "Welcome to Folk.",
  "We're so glad you're here, and even more excited to begin this journey with you.",
  "We started Folk because we believe we are in the business of homes for a reason.",
  "We deeply believe that a home should be an extension of your creative expression. We believe that creativity exists in all of us, and that your home should give you the space and freedom to express it.",
  "Because spaces matter. You matter. And creative expression matters.",
  "We believe in the power of design, but we believe just as much in the power of your personality. And sometimes, we believe in the power of practicality too.",
  "Unlike a traditional interior design process, we don't believe that the pursuit of the perfect home should come at the expense of real life. Your home needs to work for you: for your life stage, your family, your routines, your quirks and all the things that make your life yours.",
  "One of the beliefs that brought us into the business of homes is that the spaces around us deeply shape who we are and who we become.",
  "So, if you love to read, we want to make sure you have room for reading.",
  "If your child loves to paint, we want to make sure there is room for painting.",
  "If you love hosting friends, we want to make sure your home makes room for that too.",
  "And yes, we want to make room for design.",
  "Think of us as your design team, your decision-making sounding board and people who genuinely care about you and your life in your home.",
  "Our hope is that we can create a home that you enjoy not just aesthetically, but practically too: a home that feels deeply personal, positive, warm and completely reflective of who you are and who you want to be.",
  "Thank you for trusting us with it.",
  "We can't wait to see where we go together.",
  "Warmly,\nNupur & Karn\nFounders, Folk",
];

export const DESIGN_PROCESS = {
  eyebrow: "The Folk Design Process",
  title: "We unravel your home in layers.",
  intro: [
    "A home doesn't come together all at once.",
    "We start with you, build the framework, bring the design to life, refine the details, make it, and finally put it all together.",
    "Think of it as a journey from You → Layout → Design → Refine → Make → Reveal.",
  ],
  phases: [
    {
      num: "01",
      name: "You",
      subtitle: "Understanding you before designing for you.",
      body: "The first layer is you. We begin with a deep understanding of your routines, your personal aesthetic, your style and the needs of you and your family.",
      details: [
        {
          heading: "The Customer Understanding Survey",
          body: "Our first step is a detailed survey that helps us understand:",
          list: [
            "How you live and use your home",
            "Your routines and habits",
            "Your personal aesthetic",
            "Your likes, dislikes and references",
            "Your family's needs",
            "What you need your home to do for you",
          ],
          note: "Our recommendation? Make a date out of it. Grab a coffee, sit with your family and do it together. Talk, debate, disagree, discover things about each other, and have fun with it. The better we understand you, the better we can design for you.",
        },
        {
          heading: "Your Pinterest Board",
          body: "Once you share your responses, our team will create a shared Pinterest board. We'll pre-populate it with your references, along with things we've selected based on our understanding of your taste.",
          list: ["You then join us on the board and tell us: \u201CYes, that's us.\u201D", "Once we're aligned, we move to the next layer."],
        },
        {
          heading: "Mood + Colour",
          body: "We'll translate this visual direction into a broader mood board and colour palette that sets the tone for the project.",
          note: "Then we move from understanding you to figuring out how you live.",
        },
      ],
    },
    {
      num: "02",
      name: "Layout",
      subtitle: "Where all the notes start coming together.",
      body: "We think about spaces a little like music. Different notes come together to make a tune. In much the same way, furniture, rugs, lighting, art and movement need to come together to make a space work.",
      details: [
        {
          heading: "Layout Library",
          body: "We take your aesthetic vision and your family's needs and create a Layout Library. For each space, we'll present up to three layout options, exploring:",
          list: [
            "Furniture placement",
            "Rug layouts",
            "Lighting",
            "Art placement",
            "Spatial relationships",
          ],
          note: "At this stage, these are layouts, not final designs. Once you choose your direction, we move to site.",
        },
        {
          heading: "Site Visit",
          body: "We'll meet with you and your interior team to review the layouts, check dimensions and align on the practicalities.",
          note: "The objective is simple: both teams should be working from the same page before we start designing.",
        },
      ],
    },
    {
      num: "03",
      name: "Design",
      subtitle: "Now we make it real.",
      body: "This is where things get exciting. We bring your approved layouts and your mood board together to create customised space renders. We'll combine pieces from the Folk catalogue with custom-made furniture and elements that we think will work beautifully for you.",
      details: [
        {
          heading: "Space Renders",
          body: "The renders will be detailed to the tee: including furniture, rugs, lighting, art, and all the important details in between.",
          note: "You'll have up to two iterations of the renders. This is the point where we want your input. Tell us what works. Tell us what doesn't. Once the design is confirmed, we move into production, so this is the time to get the big decisions right.",
        },
      ],
    },
    {
      num: "04",
      name: "Refine",
      subtitle: "The little things are actually the big things.",
      body: "While your products are moving into production, we continue working on the finer details. This is where we have some fun.",
      details: [
        {
          heading: "Together, we'll select",
          list: [
            "Fabrics",
            "Wall colours",
            "Artwork",
            "Decorative objects",
            "Custom-made pieces",
            "Other finishing details",
          ],
          note: "This phase happens in parallel with production, so we're making the most of the time while your furniture is being made.",
        },
      ],
    },
    {
      num: "05",
      name: "Production",
      subtitle: "From screen to real life.",
      body: "Once everything is confirmed, your products go into production. Production is typically the longest phase of the journey and takes approximately 8–10 weeks.",
      details: [
        {
          heading: "During production",
          body: "Our team won't be as involved day-to-day, but we're still here. If you have questions, concerns or decisions that need to be made, you can reach us through your Folk point of contact.",
          note: "As we get closer to completion, we'll work with you to confirm your delivery date. And then\u2026 it's time to bring it all home.",
        },
      ],
    },
    {
      num: "06",
      name: "Style + Reveal",
      subtitle: "The final layer.",
      body: "On your agreed installation date, we bring your furniture and products together at your home. As part of your Folk package, we'll provide a complimentary stylist to help bring everything together.",
      details: [
        {
          heading: "Installation includes",
          list: [
            "Furniture installation",
            "Product placement",
            "Styling",
            "Art installation",
            "The final details that make the space feel complete",
          ],
          note: "Installation and styling typically take 24–48 hours. And then we call it: the reveal. You walk into your home. Everything is where it belongs. And you get to start living in it.",
        },
        {
          heading: "After the reveal",
          body: "Our relationship doesn't have to end on installation day. Once you've had some time to live in your home, we'd love to hear how it feels: what you love, what surprised you, what works beautifully.",
          note: "And, if you're open to it, we'd love to share your story as part of Real Homes: our series celebrating the people and lives behind the spaces we create.",
        },
      ],
    },
  ],
};

export const ROADMAP = {
  eyebrow: "Your Folk Project Roadmap",
  title: "A little map of where we're going.",
  intro: "Every home is different, but here's the standard roadmap for a comprehensive, ready-to-move-in home revamp with Folk.",
  phases: [
    { num: "01", name: "Contact & Discovery", body: "Understanding you, your family and your aesthetic", weeks: "2 weeks" },
    { num: "02", name: "Layout Closure", body: "Creating and confirming your layouts", weeks: "2 weeks" },
    { num: "03", name: "Design", body: "Developing detailed space renders", weeks: "2 weeks" },
    { num: "04", name: "Refinement", body: "Finalising fabrics, colours, art & details", weeks: "2 weeks" },
    { num: "05", name: "Production", body: "Making and preparing your products", weeks: "8–10 weeks" },
    { num: "06", name: "Styling & Reveal", body: "Installation, styling & art", weeks: "1 week" },
  ],
  totalLine: "In total: approximately 17–19 weeks from project commencement to reveal.",
  note: "The first four phases are driven collaboratively between you and the Folk team. Production and delivery are different. Once production begins, the final delivery and installation dates are dependent on manufacturing, vendor schedules, logistics and site readiness. As a result, the final delivery or installation may move from the indicative roadmap. We'll keep you updated and work with you towards a confirmed delivery and installation date.",
  simple: [
    { weeks: "2 weeks", label: "Discover" },
    { weeks: "2 weeks", label: "Layout" },
    { weeks: "2 weeks", label: "Design" },
    { weeks: "2 weeks", label: "Refine" },
    { weeks: "8–10 weeks", label: "Make" },
    { weeks: "1 week", label: "Style" },
    { weeks: "", label: "Reveal." },
  ],
};

export const WORKING_TOGETHER = {
  eyebrow: "Working Together",
  title: "The best homes are made together.",
  intro: "A great design process isn't just about what Folk does. It's about how we work together. Here's what you can expect from us, and what we need from you to make this process as smooth, enjoyable and successful as possible.",
  expectations: [
    { num: "01", name: "Regular check-ins", body: "We'll schedule weekly or biweekly project reviews, depending on what the project requires. These can happen over Google Meet or in person, if an in-person meeting is needed." },
    { num: "02", name: "A clear line of communication", body: "You'll be part of your Folk communication channels through WhatsApp and/or email, depending on your preference. You'll also have a dedicated point of contact for your project who will coordinate your requirements and keep things moving. As the project progresses, your point of contact may transition from the design team to the production and delivery team. You'll typically have three key Folk contacts across the project, with the founders also available when needed." },
    { num: "03", name: "A project success team", body: "We have a dedicated project success manager working behind the scenes to make sure the process, from design through production, delivery and installation, stays coordinated. That's an important part of the service we're offering you." },
    { num: "04", name: "Market visits & sourcing", body: "Where useful, we can also arrange market visits during the onboarding and early stages of the project. These can be used to compare products and vendors, check materials, explore alternatives, visit relevant vendors or pick up products. Sometimes the best answer isn't sitting in a catalogue. We're happy to go looking for it." },
    { num: "05", name: "Vendor coordination", body: "We work with a carefully selected network of vendors and makers. Where relevant, we'll help you explore ideas from other sources too, and our team can help assess, visualise and work through specific ideas you may already have." },
    { num: "06", name: "Made-to-order products", body: "Many of your Folk products are made to order through our selected vendors. This means your pieces are being created specifically for your project rather than simply picked off a shelf. For custom designs, we'll do our best to work within your approved colour palette, aesthetic and overall design direction." },
    { num: "07", name: "Delivery + installation", body: "Your Folk products are handled as part of the service, with shipping and installation coordinated by us. Our goal is to make the journey from selection to installation as seamless as possible." },
    { num: "08", name: "Styling support", body: "Once everything arrives, we'll help bring it all together. Your package includes a complimentary styling service for the products purchased through Folk." },
    { num: "09", name: "30 days of post-installation support", body: "Once your home is installed, our customer support team will remain available for 30 days to help with any queries or issues relating to the installation and products." },
  ],
  needs: [
    { num: "01", name: "One decision-making voice", body: "We work best when we have a clear point of contact from the homeowner side. Please nominate one primary decision-maker who can consolidate feedback and approvals from the family. It keeps the process moving, and saves everyone from a very long WhatsApp thread." },
    { num: "02", name: "Timely decisions", body: "The project works on a defined sequence. We therefore need your feedback and approvals within the agreed timelines so that the next stage can begin without unnecessary delays." },
    { num: "03", name: "Clear feedback", body: "Tell us what you love. Tell us what you don't. Tell us when something doesn't feel like you. We'd much rather have an honest conversation early than discover it after something has gone into production." },
    { num: "04", name: "Timely payments", body: "Your project is structured around three payment milestones. Payments are made upfront at the agreed stages so that we can reserve the required design, project management, production and styling resources for you. Once your products and selections are finalised, we'll share the final product quote with you. The subsequent milestones are linked to moving the project into production and completing the project for delivery. The exact payment schedule and amounts will be detailed separately in your commercial documents and invoice." },
    { num: "05", name: "Trust the process", body: "There will be moments when there are a lot of decisions. That's normal. Our job is to help you make those decisions, not overwhelm you with them. Use us as your design sounding board. Bring us the things you've found, the things you're unsure about and even the things you really want but don't know how to make work. We'll figure them out together." },
  ],
  outro: {
    title: "One last thing",
    body: "We want this to be fun. Yes, there will be timelines. Yes, there will be decisions. Yes, there will be furniture, measurements, production schedules and the occasional spreadsheet. But at the end of it, we're making a home. Your home. So let's make something beautiful, personal, practical and completely yours.",
    closing: "Welcome to Folk. Let's make room for you.",
  },
  documents: [
    { num: "01", name: "Homeowner Welcome Docket", body: "Your introduction to Folk, our process and how we'll work together." },
    { num: "02", name: "Terms & Conditions Agreement", body: "The formal terms governing the project, scope, payments, production, delivery and other applicable conditions." },
    { num: "03", name: "Project Invoice", body: "Your invoice and applicable payment milestones for the project." },
  ],
};

export function parseSalutation(contactName) {
  const raw = (contactName || "").trim();
  if (!raw) return { salutation: "Dear friend", displayName: "friend", firstNames: [] };

  // Split by "&" or " and " (case-insensitive)
  const parts = raw.split(/\s*&\s*|\s+and\s+/i).map((s) => s.trim()).filter(Boolean);
  // First name of each chunk (drops shared surname on the last chunk)
  const firsts = parts.map((p) => p.split(/\s+/)[0]);
  const displayName = firsts.length > 1
    ? firsts.slice(0, -1).join(", ") + " and " + firsts[firsts.length - 1]
    : firsts[0];
  return {
    salutation: `Dear ${displayName}`,
    displayName,
    firstNames: firsts,
  };
}
